﻿import React from "react";
import { NO_SEARCH_RESULTS, TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import { concatenateName, DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import { LogsTableSkeletonDesktop, LogsTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { NO_LOGS_FOUND } from "@/utils/constants/messages";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import { MobileDateField } from "@/components/cards/MobileDateField";

interface LogsTableProps {
    logs: [];
    setLogs: (roles: any) => void;
    isLoading: boolean;
    dataLength: number;
    sorting: string;
    searchItem: string | null;
    allFetched?: boolean;
    setSorting: ({}) => void;
    setPageNo: (pageNo: number) => void;
    dataLoad: boolean;
    id: string;
}

const formatLogCategory = (category: string) => {
    if (!category) return "";
    return category.replace(/_/g, " ");
};

const capitalizeAction = (value: string) => {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const getLogCategoryClass = (category: string) => {
    switch ((category || "").toLowerCase()) {
        case "error":
        case "danger":
            return "badge-outline-danger";
        case "warning":
            return "badge-outline-warning";
        case "success":
            return "badge-outline-success";
        case "info":
            return "badge-outline-info";
        default:
            return "badge-outline-secondary";
    }
};

const splitActionOn = (full: string) => {
    if (!full || full === "N/A") {
        return { label: "N/A", detail: null as string | null };
    }
    const separator = full.indexOf(": ");
    if (separator === -1) {
        return { label: full, detail: null };
    }
    return {
        label: full.slice(0, separator + 1),
        detail: full.slice(separator + 2),
    };
};

const LogsTable: React.FC<LogsTableProps> = (props) => {
    const [createdAtStatus, setCreatedAtStatus] = React.useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");
    const isCompactLayout = useCompactListLayout();
    const logs = Array.isArray(props.logs) ? props.logs : [];
    const isPendingInitialLoad = props.dataLoad || (!logs.length && props.isLoading);
    const isLoadingMore = props.isLoading && !props.dataLoad;

    const createdAtSort = () => {
        setCreatedAtStatus((prevStatus) => !prevStatus);
        setSortElement("created_at");
        props.setPageNo(1);
        props.setSorting({ created_at: createdAtStatus ? 1 : -1 });
    };

    const extractActionOn = (obj: any) => {
        return obj?.envelope || obj?.envelopes ? `Envelope: ${obj?.envelope || obj?.envelopes}` : 
            obj?.form || obj?.forms ? `Form: ${obj?.form || obj?.forms}` : 
            obj?.template || obj?.templates ? `Template: ${obj?.template || obj?.templates}` :
            obj?.comment ? `Comment: ${obj?.comment}` : 
            obj?.delegate ? `Delegate: ${obj?.delegate}`:
            obj?.user ? `User: ${obj?.user}` :
            obj?.contact? `Contact: ${obj.contact}`: 
            obj?.email_address ? `User: ${obj?.email_address}` :
            obj?.role ? `Role: ${obj?.role}`:
            obj?.group ? `Group: ${obj?.group}`:
            obj?.documents ? `Document(s): ${Array.isArray(obj?.documents)  ? obj?.documents?.join(', ') : obj?.documents}`:
            obj?.document ? `Document: ${obj?.document}`:
            "N/A";
    };

    if (isCompactLayout) {
        if (isPendingInitialLoad) {
            return <LogsTableSkeletonMobile />;
        }

        if (logs.length > 0) {
            return (
                <div className="envelope_table gs-logs-mobile">
                    {logs.map((log: any, key: number) => (
                        <div className="envelope_tr gs-logs-mobile__row" key={log.id}>
                            <div className="env_meta_data gs-logs-mobile__body w-100">
                                <div className="text-dark gs-logs-mobile__line">
                                    <span className="fw-bold">Action By: </span>
                                    {log?.action_by?.first_name && log?.action_by?.last_name ? (
                                        <span className="text-dark font-weight-normal" id={`${props.id}-action-by-name-${key}-mb`}>
                                            {concatenateName(log.action_by.first_name, log.action_by.last_name)}
                                        </span>
                                    ) : null}
                                    {log?.action_by?.email_address?.trim() ? (
                                        <span className="text-soft" id={`${props.id}-action-by-email-${key}-mb`}>
                                            {log?.action_by?.first_name && log?.action_by?.last_name
                                                ? ` (${log.action_by.email_address})`
                                                : `(${log.action_by.email_address})`}
                                        </span>
                                    ) : null}
                                    {!(
                                        (log?.action_by?.first_name && log?.action_by?.last_name) ||
                                        log?.action_by?.email_address?.trim()
                                    ) ? (
                                        <span className="text-dark font-weight-normal" id={`${props.id}-action-by-${key}-mb`}>
                                            N/A
                                        </span>
                                    ) : null}
                                </div>
                                <div className="text-dark gs-logs-mobile__line gs-logs-mobile__line--spaced">
                                    <span className="fw-bold">Action On: </span>
                                    <span className="tb-lead-sub gs-logs-mobile__inline-value" id={`${props.id}-action-on-${key}-mb`}>
                                        {extractActionOn(log.action_data)}
                                    </span>
                                </div>
                                <div className="text-dark gs-logs-mobile__line gs-logs-mobile__line--spaced">
                                    <span className="fw-bold">Action: </span>
                                    <span className="tb-lead-sub gs-logs-mobile__inline-value" id={`${props.id}-action-${key}-mb`}>
                                        {capitalizeAction(log.action)}
                                    </span>
                                </div>
                                <div className="text-dark gs-logs-mobile__line gs-logs-mobile__line--spaced">
                                    <span className="fw-bold">Message: </span>
                                    <span className="tb-lead-sub gs-logs-mobile__inline-value" id={`${props.id}-message-${key}-mb`}>
                                        {log?.message}
                                    </span>
                                </div>
                                <div className="text-dark gs-logs-mobile__line gs-logs-mobile__line--spaced">
                                    <span className="fw-bold">Category: </span>
                                    <span
                                        className={`badge badge-dim badge-sm text-capitalize gs-logs-category-badge ${getLogCategoryClass(log.category)}`}
                                        id={`${props.id}-category-${key}-mb`}
                                    >
                                        {formatLogCategory(log?.category)}
                                    </span>
                                </div>
                                <MobileDateField
                                    label="Created At: "
                                    value={log.created_at}
                                    dateId={`${props.id}-created-at-date-${key}-mb`}
                                    timeId={`${props.id}-created-at-time-${key}-mb`}
                                />
                            </div>
                        </div>
                    ))}
                    {isLoadingMore ? <LogsTableSkeletonMobile rowCount={2} wrap={false} /> : null}
                </div>
            );
        }

        if (props.searchItem) {
            return (
                <NoDataAvailable>
                    <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                </NoDataAvailable>
            );
        }

        return (
            <NoDataAvailable>
                <h4 className="center" id={`${props.id}-no-data-mb`}>
                    {NO_LOGS_FOUND}
                </h4>
            </NoDataAvailable>
        );
    }

    return (
        <table
            className="gs-users-table gs-logs-table"
            style={{ tableLayout: "fixed", width: "100%" }}
        >
            <colgroup>
                <col className="gs-logs-col-created" />
                <col className="gs-logs-col-action" />
                <col className="gs-logs-col-action-by" />
                <col className="gs-logs-col-action-on" />
                <col className="gs-logs-col-message" />
                <col className="gs-logs-col-category" />
            </colgroup>
            <thead>
                <tr>
                    <th
                        className="gs-users-th gs-logs-col-created cursor-pointer"
                        onClick={createdAtSort}
                    >
                        <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                        <em
                            id={`${props.id}-created-at-th-sort-icon`}
                            className={`ml-1 icon ni ${sortElement === "created_at" ? (createdAtStatus !== true ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                        />
                    </th>
                    <th className="gs-users-th gs-logs-col-action">
                        <span id={`${props.id}-action-th`}>{TABLE_HEADERS.ACTION}</span>
                    </th>
                    <th className="gs-users-th gs-logs-col-action-by">
                        <span id={`${props.id}-action-by-th`}>{TABLE_HEADERS.ACTION_BY}</span>
                    </th>
                    <th className="gs-users-th gs-logs-col-action-on">
                        <span id={`${props.id}-action-on-th`}>{TABLE_HEADERS.ACTION_ON}</span>
                    </th>
                    <th className="gs-users-th gs-logs-col-message">
                        <span id={`${props.id}-message-th`}>{TABLE_HEADERS.MESSAGE}</span>
                    </th>
                    <th className="gs-users-th gs-logs-col-category">
                        <span id={`${props.id}-category-th`}>{TABLE_HEADERS.CATEGORY}</span>
                    </th>
                </tr>
            </thead>
            <tbody>
                {!isPendingInitialLoad ? (
                    logs.length > 0 ? (
                        <>
                            {logs.map((log: any, key: number) => (
                                <tr key={log.id}>
                                    <td className="gs-users-td gs-logs-col-created">
                                        <div className="gs-logs-date-cell">
                                            <span className="gs-users-table-date" id={`${props.id}-created-at-date-${key}`}>
                                                {DateFormatter(log?.created_at)}
                                            </span>
                                            <span className="gs-users-table-date-sub" id={`${props.id}-created-at-time-${key}`}>
                                                {TimeFormatter(log?.created_at)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="gs-users-td gs-logs-col-action">
                                        <div className="gs-logs-action-cell">
                                            <span className="gs-logs-action-text" id={`${props.id}-action-${key}`}>
                                                {capitalizeAction(log.action)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="gs-users-td gs-logs-col-action-by">
                                        <div className="gs-logs-action-by-cell">
                                            <span className="gs-logs-action-by-name" id={`${props.id}-action-by-name-${key}`}>
                                                {log?.action_by?.first_name && log?.action_by?.last_name
                                                    ? concatenateName(log.action_by.first_name, log.action_by.last_name)
                                                    : "N/A"}
                                            </span>
                                            <span className="gs-logs-action-by-email" id={`${props.id}-action-by-email-${key}`}>
                                                {log?.action_by?.email_address ? log.action_by.email_address : "N/A"}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="gs-users-td gs-logs-col-action-on">
                                        {(() => {
                                            const { label, detail } = splitActionOn(extractActionOn(log.action_data));
                                            return (
                                                <div className="gs-logs-action-on-cell">
                                                    <span className="gs-logs-action-on-label" id={`${props.id}-action-on-${key}`}>
                                                        {label}
                                                    </span>
                                                    {detail ? (
                                                        <>
                                                            {" "}
                                                            <span
                                                                className="gs-logs-action-on-detail"
                                                                id={`${props.id}-action-on-detail-${key}`}
                                                            >
                                                                {detail}
                                                            </span>
                                                        </>
                                                    ) : null}
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td className="gs-users-td gs-logs-col-message">
                                        <span className="gs-logs-message-text" id={`${props.id}-message-${key}`}>
                                            {log.message}
                                        </span>
                                    </td>
                                    <td className="gs-users-td gs-logs-col-category">
                                        <span
                                            className={`badge badge-dim badge-sm text-capitalize gs-logs-category-badge ${getLogCategoryClass(log.category)}`}
                                            id={`${props.id}-category-${key}`}
                                        >
                                            {formatLogCategory(log?.category)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {isLoadingMore ? <LogsTableSkeletonDesktop rowCount={2} /> : null}
                        </>
                    ) : props.searchItem ? (
                        <tr>
                            <td colSpan={6}>
                                <NoDataAvailable>
                                    <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                                </NoDataAvailable>
                            </td>
                        </tr>
                    ) : (
                        <tr>
                            <td colSpan={6}>
                                <NoDataAvailable>
                                    <h4 className="text-center" id={`${props.id}-no-data`}>
                                        {NO_LOGS_FOUND}
                                    </h4>
                                </NoDataAvailable>
                            </td>
                        </tr>
                    )
                ) : (
                    <LogsTableSkeletonDesktop />
                )}
            </tbody>
        </table>
    );
};

export default LogsTable;
